import { ReactNode } from 'react';
import { email, required } from 'react-admin';
import { phoneNumber } from '@/src/shared/validation/validation';
import {
  Box,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { LLButton } from '@/components-library';
import CommonTextInput from '@/src/components/inputs/CommonTextInput';
import CommonPhoneInput from '@/src/components/inputs/CommonPhoneInput';
import { ClubDetailsFields, ClubDetailsLabels } from '@/src/shared/constants/clubDetails';

type ClubCreateFormProps = { 
  isLoading?: boolean; 
  children?: ReactNode;
  onlyClubCreate?: boolean;
};

const PersonalInformation = () => {
  const isMobile = useMediaQuery('(max-width: 550px)');
  const sx = {
    input: {
      mb: 1,
      '& .MuiFormControl-root': {
        mb: 0,
      },
    },
    container: {
      width: { xs: '100%', sm: '400px' },
      maxWidth: '400px',
      m: 'auto',
      mb: 5,
      px: isMobile ? 2 : 0,
    },
  };

  return (
    <Box sx={sx.container}>
      <CommonTextInput
        label={ClubDetailsLabels[ClubDetailsFields.OwnerName]}
        source={`club.${ClubDetailsFields.OwnerName}`}
        validate={[required()]}
        sx={sx.input}
        fullWidth
      />
      <CommonTextInput
        label={ClubDetailsLabels[ClubDetailsFields.OwnerEmail]}
        source={`club.${ClubDetailsFields.OwnerEmail}`}
        validate={[required(), email()]}
        sx={sx.input}
        fullWidth
      />
      <CommonTextInput
        label={ClubDetailsLabels[ClubDetailsFields.DirectorName]}
        source={`club.${ClubDetailsFields.DirectorName}`}
        validate={[required()]}
        sx={sx.input}
        fullWidth
      />
      <CommonTextInput
        label={ClubDetailsLabels[ClubDetailsFields.DirectorEmail]}
        source={`club.${ClubDetailsFields.DirectorEmail}`}
        validate={[required(), email()]}
        sx={sx.input}
        fullWidth
      />
      <CommonPhoneInput
        label={ClubDetailsLabels[ClubDetailsFields.DirectorPhoneNumber]}
        source={`club.${ClubDetailsFields.DirectorPhoneNumber}`}
        validate={[required(), phoneNumber]}
        fullWidth
        ignorePolicies
      />
    </Box>
  );
};

const ClubCreateForm = (props: ClubCreateFormProps) => {
  const isMobile = useMediaQuery('(max-width: 550px)');
  const sx = {
    box: {
      width: '100%',
      margin: 'auto',
      textAlign: 'center',
      backgroundColor: 'rgb(255, 255, 255)',
      color: 'rgba(0, 0, 0, 0.87)',
      px: { xs: 2, sm: 3 },
      py: 0,
    },
    boxMobile: {
      width: 'unset',
    },
    form: {
      flex: 1,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      maxWidth: '952px',
      margin: 'auto',
      p: { xs: 1, sm: 0 },
      '& .MuiCardContent-root': {
        px: 0,
      },
    },
    toolbarBtn: {
      minWidth: '130px',
    },
    buttonContainer: {
      maxWidth: '952px',
      margin: 'auto',
      pb: 4,
      mt: -2,
      gap: 2,
      display: 'flex',
      justifyContent: 'center',
      px: { xs: 2, sm: 0 },
    },
    title: {
      textAlign: 'center',
      my: { xs: 2, sm: 3 },
      color: 'primary.main',
      px: { xs: 2, sm: 0 },
      fontSize: { xs: '1.5rem', sm: '2.125rem' },
    },
  };

  return (
    <Box sx={sx.form}>
      <Typography sx={sx.title} variant="h3">
        Enter Club Details
      </Typography>
      <Box sx={[sx.box, isMobile && sx.boxMobile]}>
        <PersonalInformation />
      </Box>
      {props.children}
      <Box sx={sx.buttonContainer}>
        <LLButton type="submit" sx={sx.toolbarBtn} isLoading={props.isLoading}>
          {props.onlyClubCreate ? 'SAVE' : 'CONTINUE'}
        </LLButton>
      </Box>
    </Box>
  );
};

export default ClubCreateForm;
